import React, { useCallback, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { Button, Input, Modal, Select, Spin, Upload } from 'antd';
import type { UploadFile } from 'antd';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import { colorConstants } from '../../config/colorConstants';

export type JiraPriority = 'Critical' | 'High' | 'Medium' | 'Low' | 'Lowest';

export const JIRA_PRIORITY_CODE: Record<JiraPriority, number> = {
    Critical: 1,
    High: 2,
    Medium: 3,
    Low: 4,
    Lowest: 5,
};

export type JiraBugReportFormValues = {
    summary: string;
    description: string;
    priority: JiraPriority;
    attachments: File[];
};

const PRIORITY_OPTIONS: {
    value: JiraPriority;
    label: string;
}[] = [
    { value: 'Critical', label: 'Critical' },
    { value: 'High', label: 'High' },
    { value: 'Medium', label: 'Medium' },
    { value: 'Low', label: 'Low' },
    { value: 'Lowest', label: 'Lowest' },
];

export type JiraBugReportModalProps = {
    open: boolean;
    reporterEmail?: string;
    onCancel: () => void;
    onSubmit: (values: JiraBugReportFormValues) => Promise<void> | void;
    loading?: boolean;
    submitting?: boolean;
    maxAttachmentSizeMb?: number; // default 10
    maxAttachments?: number; // default 5
};

const MAX_DEFAULT_MB = 10;
const MAX_DEFAULT_FILES = 5;

const bytesToMb = (bytes: number) => Number((bytes / (1024 * 1024)).toFixed(2));

export type JiraBugReportModalHandle = {
    reset: () => void;
};

const JiraBugReportModal = forwardRef<JiraBugReportModalHandle, JiraBugReportModalProps>(
    (
        {
            open,
            reporterEmail,
            onCancel,
            onSubmit,
            submitting = false,
            maxAttachmentSizeMb = MAX_DEFAULT_MB,
            maxAttachments = MAX_DEFAULT_FILES,
            loading = false,
        },
        ref,
    ) => {
        useImperativeHandle(ref, () => ({
            reset: () => {
                setSummary('');
                setDescription('');
                setPriority('Medium');
                setFileList([]);
                setTouched({});
            },
        }));

        const [summary, setSummary] = useState<string>('');
        const [description, setDescription] = useState<string>('');
        const [priority, setPriority] = useState<JiraPriority>('Medium');
        const [fileList, setFileList] = useState<UploadFile[]>([]);
        const [touched, setTouched] = useState<{ summary?: boolean; description?: boolean }>({});

        const remainingSlots = useMemo(
            () => Math.max(0, maxAttachments - fileList.length),
            [fileList.length, maxAttachments],
        );

        const beforeUpload = useCallback(
            (file: any) => {
                const withinLimit = bytesToMb(file.size) <= maxAttachmentSizeMb;
                return withinLimit ? true : Upload.LIST_IGNORE;
            },
            [maxAttachmentSizeMb],
        );

        const onChangeUpload = useCallback(
            (info: { fileList: UploadFile[] }) => {
                setFileList(info.fileList.slice(0, maxAttachments));
            },
            [maxAttachments],
        );

        const hasSummaryError = touched.summary && summary.trim().length === 0;
        const hasDescriptionError = touched.description && description.trim().length === 0;
        const isSubmitDisabled = submitting || summary.trim().length === 0 || description.trim().length === 0;

        const handleOk = useCallback(async () => {
            if (isSubmitDisabled) return;
            const cleanFiles = (fileList || []).map((f: any) => f.originFileObj).filter(Boolean) as File[];
            await onSubmit({
                summary: summary.trim(),
                description: description.trim(),
                priority,
                attachments: cleanFiles,
            });
        }, [fileList, description, isSubmitDisabled, onSubmit, priority, summary]);

        const footer = useMemo(
            () => (
                <div className="flex justify-end gap-3">
                    <Button style={{ borderRadius: '6px' }} onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button
                        style={{ borderRadius: '6px' }}
                        type="primary"
                        onClick={handleOk}
                        disabled={isSubmitDisabled}
                        loading={submitting}
                    >
                        {!loading ? (
                            'Submit Bug Report'
                        ) : (
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
                        )}
                    </Button>
                </div>
            ),
            [handleOk, isSubmitDisabled, loading, onCancel, submitting],
        );

        return (
            <Modal open={open} onCancel={onCancel} footer={footer} width={720} centered destroyOnClose>
                <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                        <h2 className="text-xl font-semibold">Report a Bug</h2>
                        {reporterEmail ? (
                            <span className="text-xs text-gray-500">Reporter: {reporterEmail}</span>
                        ) : null}
                    </div>

                    <label className="block text-sm font-medium mb-1">
                        Summary <span style={{ color: colorConstants.redAlt }}>*</span>
                    </label>
                    <Input
                        placeholder="Brief description of the issue"
                        value={summary}
                        onChange={(e: any) => setSummary(e.target.value)}
                        onBlur={() => setTouched((t) => ({ ...t, summary: true }))}
                        status={hasSummaryError ? 'error' : undefined}
                    />

                    <label className="block text-sm font-medium mt-4 mb-1">
                        Description <span style={{ color: colorConstants.redAlt }}>*</span>
                    </label>
                    <Input.TextArea
                        placeholder="Detailed description of the issue, steps to reproduce, and expected behavior"
                        value={description}
                        autoSize={{ minRows: 4, maxRows: 8 }}
                        onChange={(e: any) => setDescription(e.target.value)}
                        onBlur={() => setTouched((t) => ({ ...t, description: true }))}
                        status={hasDescriptionError ? 'error' : undefined}
                    />

                    <label className="block text-sm font-medium mt-4 mb-1">Priority</label>
                    <Select<JiraPriority>
                        className="w-full"
                        placeholder="Select priority"
                        value={priority}
                        onChange={(v: JiraPriority) => setPriority(v)}
                        optionLabelProp="label"
                    >
                        {PRIORITY_OPTIONS.map((opt) => {
                            const optionContent = <span className="flex items-center gap-2">{opt.label}</span>;
                            return (
                                <Select.Option key={opt.value} value={opt.value} label={optionContent}>
                                    {optionContent}
                                </Select.Option>
                            );
                        })}
                    </Select>

                    <div className="flex items-center justify-between mt-5 mb-2">
                        <label className="block text-sm font-medium">Attachment (Image or Video)</label>
                    </div>

                    <Upload.Dragger
                        name="files"
                        multiple
                        accept="image/*,video/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv"
                        fileList={fileList}
                        beforeUpload={beforeUpload}
                        onChange={onChangeUpload}
                        maxCount={maxAttachments}
                        listType="picture"
                        customRequest={() => {
                            setFileList((prev) => {
                                return prev.map((file) => {
                                    return {
                                        ...file,
                                        status: 'done',
                                    };
                                });
                            });
                            console.log('File uploaded successfully.');
                        }}
                    >
                        <p className="ant-upload-drag-icon">
                            <UploadOutlined
                                style={{
                                    color: '#A9A9A9',
                                }}
                            />
                        </p>
                        <div className="text-sm">
                            Drag and drop your file here, or <span className="text-primary">browse</span>
                        </div>
                        <div className="text-xs text-gray-500">
                            Supports images and videos up to {maxAttachmentSizeMb}MB
                        </div>
                    </Upload.Dragger>

                    {remainingSlots === 0 && (
                        <div className="mt-2 text-xs text-gray-500">
                            Maximum of {maxAttachments} attachments reached.
                        </div>
                    )}
                </div>
            </Modal>
        );
    },
);

export default JiraBugReportModal;
